import React from 'react';
import { EventEmitter } from 'events';
import { ConfigProvider, OrderListPopup, showElement } from '@shopee/chatbot-ui';
import { getButtonAction } from '@/utils/getButtonData';
import sessionStore from '@/stores/session';
import sendBotMessageToAgent from '@/utils/sendBotMessageToAgent';
import { OrderType, TRACKING_TYPE, INPUT_TYPE } from '@/utils/constants';
import { getPopupParams } from '@/components/ChatbotUI/PropsProtocol/orderListPopupParams';
import { getDriverOrderListPopupParams } from '@/components/ChatbotUI/PropsProtocol/DriverOrderListPopup';
import { HandlerType } from '@/components/ChatbotUI/FoodBuyerOrder';
import { handleLiveBtnClick, handleLoginBtnClick, handleRedirectionBtnClick, trackingHandler } from './btnClickHandler';
import { next } from './next';
import { addDialogue, updateDialogueInfo } from './sessionUtils';
import { ButtonAction } from './getButtonData';
import handleClickIntent from './intent/handleClickIntent';
import handleClickIntentUseMessage from './intent/handleClickIntentUseMessage';
import { agentStore, modalStore, initStore, userStore } from '@/stores';
import { openFullScreenImage } from './tools';
import handleArticleRedirectionButtonClick from './intent/handleArticleRedirectionButtonClick';
import { getChatUIProviderConfig } from './getChatUIProviderConfig';
import { getLiveAgentRecommenderInfo } from './agent';
import { getCallbackParams, GetCallbackParamsData, webformNewApiSubmitActionParams } from './common';
import { ChatUiEventsOptions } from '@/types/common';
import handleClickExpediteDelivery from './intent/handleClickExpediteDelivery';
import { getFormSchema, uploadInWebform } from '@/services/webform/api';
import {
  webformWidgetMap,
  webformHiddenFields,
  webformDisplayFields,
  webformHiddenWidgets,
  hardcodeSpecialComponent,
} from './webform';
import { isMultipleWebform, submitWebform, isMultipleWebformNewApi } from '@/services/chat';
import { All_TFE_BUYER_TITLES, formatOrder } from '@/components/ChatbotUI/PropsProtocol/OrderList';
import { REPLY_TYPE } from './responseHandler';

export const eventEmitter = new EventEmitter();

type EventActionType = {
  showUserMessage?: boolean;
  _actualText_?: string;
  func: 'redirection' | 'next' | 'showArticle' | 'toLiveAgent' | 'showOrder';
  info: {
    // 里面有组件的props，这里只定义了使用到的属性
    dialogueId: string;
    dialogueType: string;
    threadId?: string;
    threadNodeId?: string;
    listPos: number;
    data: any;
    trackingData: any;
    roundId?: string;
    threadIdV2?: string;
    orderShopInfo?: any;
  };
  taskbotInfo: any;
  task_bot_action: any;
  buttonInfo?: {
    button_text: string;
    action: {
      action_type: ButtonAction;
      task_bot_action: any;
    };
    callback_action?: {
      action_type: ButtonAction;
      task_bot_action: any;
      nextParams: any;
      successCb?: (data: any) => void;
    };
    extra_param?: {
      order_extra_info?: {
        cancel_reasons: {
          cancel_reason_code: number;
          text_key: string;
          text_value: string;
        }[];
        choose_cancel_reason: string;
        pop_up_text: string;
        pop_up_title_text: string;
        refund_destination: string;
        order_id: string;
        pop_up_window_confirm: string;
        pop_up_window_not_now: string;
      };
      guarantee_info?: {
        article: {
          title: string;
          content: string;
        };
        pop_up_content: {
          confirm_value: string;
          content: string;
          read_more: string;
          refuse_value: string;
          title: string;
        };
      };
    };
    multiple_action?: {
      action_type: ButtonAction;
      task_bot_action: any;
    };
  };
  nextParams?: any;
  url_info?: {
    url?: string;
    uri_key?: string;
    uri_extra?: any;
  };
  formstack?: string;
  live_agent?: {
    isChatbotMsgBeforeLiveAgent?: boolean;
    queue_type: number; // 1: inhouse 2: salesforce
    sf_queue: string;
    inhouse_queue: {
      entry_point_id: string;
      user_type_id: string;
      reason_code_id: string;
      enquiry_type_id: string;
    };
  };
  data?: any;
  customCallback?: GetCallbackParamsData;
  onClose?: () => void;
};
export interface EventData {
  text: string;
  multiple_action?: EventActionType;
  action: EventActionType;
}

export function trackClickAfterResponse(data: EventData, res: any) {
  const trackingData = data?.action?.info?.trackingData;
  if (trackingData) {
    const { bypassClickTracking, trackingname, trackingdataBasic } = trackingData;
    if (bypassClickTracking && trackingname && trackingdataBasic) {
      trackingHandler({
        trackingname,
        trackingdata: {
          ...trackingdataBasic,
          next_chat_dialogue_id: res?.dialogue_id,
        },
      });
    }
  }
}

eventEmitter.on('next', async (data: EventData, callback?: (data: { code: number; data: any }) => void) => {
  if (agentStore.inAgent) {
    sendBotMessageToAgent({ content: data?.action?._actualText_ || data?.text });
  } else {
    data?.action?.showUserMessage !== false
      && addDialogue({
        dialogueId: data?.action?.info?.dialogueId,
        dialogueType: data?.action?.info?.dialogueType,
        from: 'user',
        type: 'Msg',
        data: { content: data?.action?._actualText_ || data?.text },
      });
    const customCallback = getCallbackParams(data?.action?.info, data?.action?.customCallback);
    const result = await next({ message_text: data?.text, ...data.action.nextParams, ...customCallback }).catch(
      () => {},
    );
    callback?.(result);
  }
});

eventEmitter.on('getIntent', (data: EventData, options?: ChatUiEventsOptions) => {
  if (agentStore.inAgent) {
    sendBotMessageToAgent({ content: data?.text });
  } else {
    const customCallback = getCallbackParams(data?.action?.info, data?.action?.customCallback);
    const params = (data.action.nextParams || data.action) ?? {};
    const newParams = { ...params, ...customCallback };
    handleClickIntent(
      data?.text,
      newParams,
      (res) => {
        trackClickAfterResponse(data, res);
      },
      options,
    );
  }
});

eventEmitter.on('getIntentUseMessage', (data: EventData, callback?: (res: any) => void) => {
  if (agentStore.inAgent) {
    sendBotMessageToAgent({ content: data?.text });
  } else {
    const customCallback = getCallbackParams(data?.action?.info, data?.action?.customCallback);
    const params = (data.action.nextParams || data.action) ?? {};
    const newParams = { ...params, ...customCallback };
    handleClickIntentUseMessage(data?.text, newParams, callback);
  }
});

eventEmitter.on('clickIntent', (data: EventData) => {
  if (agentStore.inAgent) {
    sendBotMessageToAgent({ content: data?.text });
  } else {
    const customCallback = getCallbackParams(data?.action?.info, data?.action?.customCallback);
    const params = (data.action.nextParams || data.action) ?? {};
    const newParams = { ...params, ...customCallback };
    handleClickIntent(data?.text, newParams, (res: any) => {
      trackClickAfterResponse(data, res);
    });
  }
});
// 打开 webform 表单
eventEmitter.on('popupWebform', async (data: EventData, callback) => {
  const { action, multiple_action } = data;
  // 判断是否为 multiple form
  if (multiple_action?.buttonInfo?.multiple_action) {
    const customCallback = getCallbackParams(data?.action?.info, data?.action?.customCallback);
    const multipleRes = await isMultipleWebformNewApi({
      ...customCallback,
      dialogue_id: multiple_action?.info?.dialogueId,
      session_id: initStore.sessionId,
      // thread_id: multiple_action?.info?.threadId,
      message_extra: { task_bot_action: multiple_action?.task_bot_action },
      message_text: data?.text,
      input_type: INPUT_TYPE.BUTTON,
    });

    // const multipleRes = await isMultipleWebform({
    //   dialogue_id: multiple_action?.info?.dialogueId,
    //   session_id: initStore.sessionId,
    //   thread_id: multiple_action?.info?.threadId,
    //   api_key: 'check_multiple_action',
    //   query_data: JSON.stringify({
    //     multiple_conditions: {
    //       ...(multiple_action?.taskbotInfo || {}),
    //       ...((multiple_action as any)?.task_bot_action?.params || {}),
    //     },
    //   }),
    // });

    // const res = JSON.parse(multipleRes?.orders);
    // 当 is_multiple_action 为 true 时 或者 非 REPLY_TYPE.TFE_MULTIPLE_WEBFORM_NEW_API 时 不执行后面步骤
    if (
      multipleRes?.replies?.[0]?.message_extra?.control_message?.msg?.is_multiple_action
      || multipleRes?.replies?.[0].reply_type !== REPLY_TYPE.TFE_MULTIPLE_WEBFORM_NEW_API
    ) {
      // 执行 next action
      // const callbackAction = multiple_action?.buttonInfo?.multiple_action;
      // if (
      //   !agentStore.inAgent
      //   && callbackAction
      //   && getButtonAction(callbackAction.action_type) === 'next'
      //   && multiple_action?.nextParams
      // ) {
      //   const customCallback = getCallbackParams(multiple_action.info, multiple_action.customCallback);
      //   next({ ...multiple_action.nextParams, ...customCallback }).then((data) => {
      //     (callbackAction as any)?.successCb?.(data);
      //   });
      // }
      // 2) 非4001 (从webform_report_data拿上报数据)

      trackingHandler({
        trackingname: TRACKING_TYPE.WEBFORM_MULTIPLE_BUTTON,
        trackingdata: {
          ...multipleRes?.replies?.[0]?.message_extra?.task_flow_text?.webform_report_data,
          ...multiple_action?.taskbotInfo,
          create_time: new Date().getTime(),
          dialogue_id: multiple_action?.info?.dialogueId,
          session_id: initStore.sessionId,
          region: COUNTRY,
          tenant_id: 1,
        },
      });
      return;
    } else {
      // 1) 4001 且不是 multiple_action

      trackingHandler({
        trackingname: TRACKING_TYPE.WEBFORM_MULTIPLE_BUTTON,
        // BE 说这种情况没有multiple_form_answer_type
        // 而且只传form id，让DS从数据库关联获取
        trackingdata: {
          is_multiple_webform: 0,
          triggered_webForm_id: multiple_action?.task_bot_action?.params?.formstack_id?.int_val,
          ...multiple_action?.taskbotInfo,
          create_time: new Date().getTime(),
          dialogue_id: multiple_action?.info?.dialogueId,
          session_id: initStore.sessionId,
          region: COUNTRY,
          tenant_id: 1,
        },
      });
    }
  }
  const { formstack } = action;

  const searchParams = new URLSearchParams(new URL(formstack as any).search);
  const params: any = {};
  let intent_id = '';
  for (const [key, value] of (searchParams as any).entries()) {
    if (key === 'intent_id') {
      intent_id = value;
    } else {
      params[key] = value;
    }
  }

  const reg = /\/webform\/(\d|[a-z]|[A-Z])+\?/i;
  const form_sn = (formstack as any)
    .match(reg)[0]
    ?.replace(/\/webform\//, '')
    .replace('?', '');
  getFormSchema({ form_sn: form_sn })
    .then((res) => {
      if (res && (res as any).session_id) {
        const formItems: any = [];
        const formData: any = {};
        const { form_schema, ui_schema } = res as any;
        const formConfig = JSON.parse(form_schema);
        const uiConfig = JSON.parse(ui_schema);
        Object.entries(formConfig.properties).forEach(([key, value]: [string, any]) => {
          if (webformHiddenWidgets.includes(uiConfig[key]['ui:widget'])) {
            return;
          }
          // 这里的UI展示逻辑
          const _hidden = uiConfig[key]['ui:hidden'];
          const component = webformWidgetMap[uiConfig[key]['ui:widget']] || 'Input';
          const getValue = () => {
            const v = params[key] || value.defaultValue;
            if (hardcodeSpecialComponent.includes(component)) {
              if (!v) {
                return [COUNTRY, ''];
              }
              if (v && typeof v === 'string') {
                return [COUNTRY, v];
              }
              return v;
            }
            return v;
          };
          const _value = getValue();
          const isInEditMode = () => {
            if (hardcodeSpecialComponent.includes(component)) {
              return webformDisplayFields[form_sn]?.includes(key) || (!_hidden && !_value[1]);
            }
            return webformDisplayFields[form_sn]?.includes(key) || (!_hidden && !_value);
          };

          const isHidden = () => {
            if (webformHiddenFields.includes(key) && hardcodeSpecialComponent.includes(component)) {
              return _hidden || !!_value[1];
            }
            return _hidden || (webformHiddenFields.includes(key) && !!_value);
          };
          formData[key] = _value;
          formItems.push({
            label: value.title,
            name: key,
            maxLength: value.maxLength,
            minLength: value.minLength,
            supporting: value.supporting,
            options: value.options,
            required: value.required,
            component: component,
            hidden: isHidden(),
            edit: uiConfig[key]['ui:edit'],
            inEditMode: isInEditMode(),
            callback: component === 'FileUpload' ? uploadInWebform : undefined,
            fileTypeAccepted: value.fileTypeAccepted,
            maxFileSize: value.maxFileSize,
            maxFileNum: value.maxFileNum,
            getData: async (value: string) => {
              const data = await submitWebform({
                dialogue_id: action?.info?.dialogueId,
                session_id: initStore.sessionId,
                thread_id: action?.info?.threadId,
                api_key: 'get_order_info_by_order_id_for_fe',
                query_data: JSON.stringify({
                  order_sn: value,
                  user_type: 1,
                  user_id: userStore.userId,
                }),
              });
              try {
                const formRes = JSON.parse(data.orders);
                const orderData = formatOrder(formRes);
                return orderData;
              } catch (e) {
                console.log(e, 'popupWebform webform order info 解析错误');
                return null;
              }
            },
            orderListProps: {
              // tabs: [
              //   { name: 'All', type: 0 },
              //   { name: 'To Pay', type: 1 },
              //   { name: 'To Ship', type: 2 },
              //   { name: 'To Receive', type: 3 },
              //   { name: 'Completed', type: 4 },
              //   { name: 'Cancelled', type: 5 },
              //   { name: 'Return Refund', type: -1 },
              // ],
              tabs: All_TFE_BUYER_TITLES,
              selectOrderText: 'Select Order',
              onLoadMore: async (props: any) => {
                const { page_num, page_size, status, user_type } = props || {};

                const data = await submitWebform({
                  dialogue_id: action?.info?.dialogueId,
                  session_id: initStore.sessionId,
                  thread_id: action?.info?.threadId,
                  api_key: 'get_other_order',
                  query_data: JSON.stringify({
                    order_status: status,
                    user_id: userStore.userId,
                    page_size,
                    page_num,
                    user_type,
                  }),
                });
                const formRes = JSON.parse(data.orders);
                if (formRes?.order_list?.length) {
                  const orderList = formRes?.order_list.map((order: any) => {
                    return formatOrder(order, { status });
                  });
                  return { data: orderList };
                }
                return { data: [] };
              },
            },
            defaultSelected: 0,
            showLoading: true,
          });
        });
        // 打开表单
        const props = {
          items: formItems,
          title: action?.buttonInfo?.button_text as string,
          intent_id,
          mode: 0,
          form_sn: form_sn,
          action: data.action,
          multiple_action: data.multiple_action,
          active: true,
          formData: formData,
        };
        callback(props);
      }
    })
    .catch((err) => {
      console.log('get form err----- ', err);
    });
});

eventEmitter.on('redirection', async (data: EventData) => {
  const { multiple_action, action } = data;
  if (multiple_action?.buttonInfo?.multiple_action) {
    const customCallback = getCallbackParams(data?.action?.info, data?.action?.customCallback);
    const multipleRes = await isMultipleWebformNewApi({
      ...customCallback,
      dialogue_id: multiple_action?.info?.dialogueId,
      session_id: initStore.sessionId,
      // thread_id: multiple_action?.info?.threadId,
      message_extra: { task_bot_action: multiple_action?.task_bot_action },
      message_text: data?.text,
      input_type: INPUT_TYPE.BUTTON,
    });
    // const multipleRes = await isMultipleWebform({
    //   dialogue_id: multiple_action?.info?.dialogueId,
    //   session_id: initStore.sessionId,
    //   thread_id: multiple_action?.info?.threadId,
    //   api_key: 'check_multiple_action',
    //   query_data: JSON.stringify({
    //     multiple_conditions: {
    //       ...(multiple_action?.taskbotInfo || {}),
    //       ...((multiple_action as any).task_bot_action?.params || {}),
    //     },
    //   }),
    // });
    // const res = JSON.parse(multipleRes?.orders);
    // 当 is_multiple_action 为 true 时 或者 非 REPLY_TYPE.TFE_MULTIPLE_WEBFORM_NEW_API 时 不执行后面步骤
    if (
      multipleRes?.replies?.[0]?.message_extra?.control_message?.msg?.is_multiple_action
      || multipleRes?.replies?.[0].reply_type !== REPLY_TYPE.TFE_MULTIPLE_WEBFORM_NEW_API
    ) {
      // 执行 next action
      // const callbackAction = multiple_action?.buttonInfo?.multiple_action;
      // if (
      //   !agentStore.inAgent
      //   && callbackAction
      //   && getButtonAction(callbackAction.action_type) === 'next'
      //   && multiple_action?.nextParams
      // ) {
      //   const customCallback = getCallbackParams(multiple_action.info, multiple_action.customCallback);
      //   next({ ...multiple_action.nextParams, ...customCallback }).then((data) => {
      //     (callbackAction as any)?.successCb?.(data);
      //   });
      // }
      // 2) 非4001 (从webform_report_data拿上报数据)

      trackingHandler({
        trackingname: TRACKING_TYPE.WEBFORM_MULTIPLE_BUTTON,
        trackingdata: {
          ...multipleRes?.replies?.[0]?.message_extra?.task_flow_text?.webform_report_data,
          ...multiple_action?.taskbotInfo,
          create_time: new Date().getTime(),
          dialogue_id: multiple_action?.info?.dialogueId,
          session_id: initStore.sessionId,
          region: COUNTRY,
          tenant_id: 1,
        },
      });
      return;
    } else {
      // 1) 4001 且不是 multiple_action

      trackingHandler({
        trackingname: TRACKING_TYPE.WEBFORM_MULTIPLE_BUTTON,
        // BE 说这种情况没有multiple_form_answer_type
        // 而且只传form id，让DS从数据库关联获取
        trackingdata: {
          is_multiple_webform: 0,
          triggered_webForm_id: multiple_action?.task_bot_action?.params?.formstack_id?.int_val,
          ...multiple_action?.taskbotInfo,
          create_time: new Date().getTime(),
          dialogue_id: multiple_action?.info?.dialogueId,
          session_id: initStore.sessionId,
          region: COUNTRY,
          tenant_id: 1,
        },
      });
    }
  }
  const btn = {
    ...{
      url: data?.action?.formstack,
      ...(data.action?.url_info || {}),
    },
    isHistory: false,
    text_key: '',
    text_value: '',
  };
  data.text
    && data?.action?.showUserMessage !== false
    && addDialogue({
      dialogueId: data?.action?.info?.dialogueId,
      dialogueType: data?.action?.info?.dialogueType,
      from: 'user',
      type: 'Msg',
      data: { content: data.text },
    });
  if (btn.url || btn.uri_key) {
    handleRedirectionBtnClick(
      btn,
      undefined, // TODO: 埋点数据
      () => {
        // 告知 BE 表单提交的动作发生
        console.log('multiple_action = ', multiple_action);
        multiple_action?.buttonInfo?.multiple_action
          && isMultipleWebform({
            dialogue_id: multiple_action?.info?.dialogueId,
            session_id: initStore.sessionId,
            thread_id: multiple_action?.info?.threadId,
            api_key: 'submit_multiple_form_status',
            query_data: JSON.stringify({
              multiple_conditions: {
                ...(multiple_action?.taskbotInfo || {}),
                ...((multiple_action as any)?.task_bot_action?.params || {}),
              },
            }),
          });

        const callbackAction = data?.action?.buttonInfo?.callback_action;
        if (
          !agentStore.inAgent
          && callbackAction
          && getButtonAction(callbackAction.action_type) === 'next'
          && callbackAction?.nextParams
        ) {
          const customCallback = getCallbackParams(data?.action?.info, data?.action?.customCallback);
          const nextParams = { ...callbackAction.nextParams, ...customCallback };

          if (
            multiple_action?.buttonInfo?.multiple_action
            && nextParams?.message_extra?.task_bot_action
            && !nextParams?.message_extra?.task_bot_action?.interaction_cmd
          ) {
            nextParams.message_extra.task_bot_action = {
              ...nextParams?.message_extra?.task_bot_action,
              ...webformNewApiSubmitActionParams,
            };
          }
          next(nextParams).then((data) => {
            callbackAction?.successCb?.(data);
            if (multiple_action?.buttonInfo?.multiple_action) {
              trackingHandler({
                trackingname: TRACKING_TYPE.WEBFORM_SUBMIT_BUTTON,
                trackingdata: {
                  ...data?.replies?.[0]?.message_extra?.task_flow_text?.webform_report_data,
                  ...multiple_action?.taskbotInfo,
                  create_time: new Date().getTime(),
                  dialogue_id: multiple_action?.info?.dialogueId,
                  session_id: initStore.sessionId,
                  region: COUNTRY,
                  tenant_id: 1,
                },
              });
            }
          });
        }
      },
      { closePopup: action?.onClose },
    );
  }
});

// https://confluence.shopee.io/display/SPAT/openExternalLink
// 打开外部链接
eventEmitter.on('openExternalLink', async (data: EventData) => {
  const info = {
    url: data?.action?.formstack ?? data?.action?.url_info?.url,
    ...(data?.action?.data ?? {}),
  };
  data.text
    && addDialogue({
      dialogueId: data?.action?.info?.dialogueId,
      dialogueType: data?.action?.info?.dialogueType,
      from: 'user',
      type: 'Msg',
      data: { content: data.text },
    });

  if (info.url) {
    const { bridgeCallFunc } = await import('@/utils/instantLoaded/tools');
    bridgeCallFunc(() => {
      window.bridgeCallHandler('openExternalLink', { ...info }, function (response: any) {
        console.log(response, '打开外部链接结果');
      });
    });
  }
});

eventEmitter.on('toLiveAgent', (data: EventData) => {
  const { userType, enquiryType, triggerLiveAgentRecommender } = getLiveAgentRecommenderInfo({
    presetUserType: data?.action?.live_agent?.inhouse_queue?.user_type_id,
    presetEnquiryType: data?.action?.live_agent?.inhouse_queue?.enquiry_type_id,
  });

  handleLiveBtnClick({
    dialogueId: data?.action?.info?.dialogueId,
    dialogueType: data?.action?.info?.dialogueType,
    text: data.text,
    listPos: data?.action?.info?.listPos,
    queue_name: data?.action?.live_agent?.sf_queue,
    user_type: userType,
    enquiry_type: enquiryType,
    isHistory: false,
    isChatbotMsgBeforeLiveAgent: data?.action?.live_agent?.isChatbotMsgBeforeLiveAgent,
    triggerLiveAgentRecommender,
  });
});

eventEmitter.on('cancelOrder', (data: EventData) => {
  const orderInfo = data?.action?.buttonInfo?.extra_param?.order_extra_info;
  const cardPosition = data?.action?.info?.listPos;
  const taskbotInfo = data?.action?.taskbotInfo;
  addDialogue(
    {
      threadId: data?.action?.info?.threadId,
      dialogueId: data?.action?.info?.dialogueId,
      dialogueType: data?.action?.info?.dialogueType,
      threadNodeId: data?.action?.info?.threadNodeId,
      from: 'bot',
      type: 'TFECancelOrder',
      data: {
        cardPosition,
        taskbotInfo,
        action: data?.action?.buttonInfo?.action,
        popup: {
          title: orderInfo?.choose_cancel_reason,
          footerText: data.text,
        },
        modal: {
          title: orderInfo?.pop_up_title_text,
          content: orderInfo?.pop_up_text,
          confirmText: orderInfo?.pop_up_window_confirm,
          cancelText: orderInfo?.pop_up_window_not_now,
        },
        options: (orderInfo?.cancel_reasons || []).map((reason) => ({
          value: reason.cancel_reason_code,
          label: reason.text_value,
        })),
        orderId: orderInfo?.order_id,
        refundDestination: orderInfo?.refund_destination,
      },
    },
    false,
  );
});

eventEmitter.on('extendShopeeGuarantee', (data: EventData) => {
  const guaranteeInfo = data?.action?.buttonInfo?.extra_param?.guarantee_info;
  const article = guaranteeInfo?.article;
  const popupContent = guaranteeInfo?.pop_up_content;
  const cardPosition = data?.action?.info?.listPos;
  const taskbotInfo = data?.action?.taskbotInfo;

  addDialogue(
    {
      threadId: data?.action?.info?.threadId,
      dialogueId: data?.action?.info?.dialogueId,
      dialogueType: data?.action?.info?.dialogueType,
      threadNodeId: data?.action?.info?.threadNodeId,
      threadIdV2: data?.action?.info?.threadIdV2,
      roundId: data?.action?.info?.roundId,
      from: 'bot',
      type: 'TFEGuaranteeModal',
      data: {
        taskbotInfo,
        cardPosition,
        action: data?.action?.buttonInfo?.action,
        modalProps: popupContent,
        article,
      },
    },
    false,
  );
});

eventEmitter.on('login', (data: EventData) => {
  handleLoginBtnClick({
    data: data,
    dialogueId: data?.action?.info?.dialogueId,
    dialogueType: data?.action?.info?.dialogueType,
    isHistory: false,
  });
});

eventEmitter.on('showOrder', (data: any) => {
  const { action: { info: { orderSn = '', returnSn = '', returnId = '' } = {}, orderType = OrderType.shopee } = {}}
    = data;

  if (orderSn) {
    sessionStore.ADD_ORDER_SN(orderSn);
  }

  if (returnId) {
    sessionStore.SET_ORDER_INFO_DATA({
      return_sn: returnSn,
      return_id: returnId,
    });
  }

  addDialogue({
    dialogueId: data?.action?.info?.dialogueId,
    dialogueType: data?.action?.info?.dialogueType,
    from: 'user',
    type: orderType === OrderType.food ? 'FoodOrder' : 'TFEOrder',
    data: { ...data?.action.info },
  });

  const customCallback = getCallbackParams(data?.action?.info, data?.action?.customCallback);
  next({ ...data.action.nextParams, ...customCallback });
});

eventEmitter.on('showFoodBuyerOrder', (data: any) => {
  addDialogue({
    dialogueId: data?.action?.info?.dialogueId,
    dialogueType: data?.action?.info?.dialogueType,
    from: 'user',
    type: 'TFEFoodBuyerOrder',
    data: { ...data?.action.info, handlerType: HandlerType.NoNeedHandleParam },
  });

  const customCallback = getCallbackParams(data?.action?.info, data?.action?.customCallback);
  next({ ...data.action.nextParams, ...customCallback });
});

eventEmitter.on('showTransaction', (data: any) => {
  addDialogue({
    dialogueId: data?.action?.info?.dialogueId,
    dialogueType: data?.action?.info?.dialogueType,
    from: 'user',
    type: 'TFETransaction',
    data: data?.action.info,
  });

  const customCallback = getCallbackParams(data?.action?.info, data?.action?.customCallback);
  next({ ...data.action.nextParams, ...customCallback });
});

eventEmitter.on('previewImage', (data: { url: string }) => {
  data?.url && openFullScreenImage(data.url);
});

eventEmitter.on('clickArticleRedirectionButton', (data) => {
  handleArticleRedirectionButtonClick(data);
});

eventEmitter.on('popupOrderList', async (data) => {
  const { default: firstInit } = await import('@/stores/firstInit');
  const { default: inputStore } = await import('@/stores/input');

  inputStore.UPDATE_INPUT_FOCUS(false);
  firstInit.UPDATE_INPUT_FOCUS(false);

  const instance = showElement({
    children: (
      <ConfigProvider {...getChatUIProviderConfig()}>
        <OrderListPopup
          onClosePopup={() => instance.destroy()}
          onSelectOrder={() => {
            instance.destroy();
          }}
          {...getPopupParams(data.action)}
        />
      </ConfigProvider>
    ),
  });
});

eventEmitter.on('autoPopupDriverOrderList', async (data) => {
  const { default: firstInit } = await import('@/stores/firstInit');
  const { default: inputStore } = await import('@/stores/input');

  inputStore.UPDATE_INPUT_FOCUS(false);
  firstInit.UPDATE_INPUT_FOCUS(false);

  const listPos = data.action?.listPos;
  if (listPos) {
    updateDialogueInfo({ shouldDisplayInstantly: false }, listPos);
  }

  const instance = showElement({
    children: (
      <ConfigProvider {...getChatUIProviderConfig()}>
        <OrderListPopup
          onClosePopup={() => instance.destroy()}
          onSelectOrder={() => {
            instance.destroy();
          }}
          {...getDriverOrderListPopupParams(data.action)}
        />
      </ConfigProvider>
    ),
  });
});

eventEmitter.on('submitExpediteDelivery', (data: EventData, callback?: (res: any) => void) => {
  if (agentStore.inAgent) {
    sendBotMessageToAgent({ content: data?.text });
  } else {
    data?.action?.showUserMessage !== false
      && addDialogue({
        dialogueId: data?.action?.info?.dialogueId,
        dialogueType: data?.action?.info?.dialogueType,
        from: 'user',
        type: 'Msg',
        data: { content: data?.action?._actualText_ || data?.text },
      });

    const params = data?.action?.data;

    const customCallback = getCallbackParams(data?.action?.info, data?.action?.customCallback);

    const nextParams = { ...data.action.nextParams, ...customCallback };
    handleClickExpediteDelivery(params, nextParams, callback);
  }
});

eventEmitter.on('expediteDelivery', (data: EventData) => {
  const orderShopInfoParams = data?.action?.buttonInfo?.action?.task_bot_action?.params;
  const order_id = orderShopInfoParams?.order_id?.int_val;
  const shop_id = orderShopInfoParams?.shop_id?.int_val;
  const orderShopInfo = { order_id, shop_id };
  const cardPosition = data?.action?.info?.listPos;
  const taskbotInfo = data?.action?.taskbotInfo;

  addDialogue(
    {
      threadId: data?.action?.info?.threadId,
      dialogueId: data?.action?.info?.dialogueId,
      dialogueType: data?.action?.info?.dialogueType,
      threadNodeId: data?.action?.info?.threadNodeId,
      threadIdV2: data?.action?.info?.threadIdV2,
      roundId: data?.action?.info?.roundId,
      from: 'bot',
      type: 'TFEExpediteDeliveryModal',
      data: {
        taskbotInfo,
        cardPosition,
        action: data?.action?.buttonInfo?.action,
        orderShopInfo,
      },
    },
    false,
  );
});

eventEmitter.on('deliveryInstructions', (data: EventData) => {
  const cardPosition = data?.action?.info?.listPos;
  const taskbotInfo = data?.action?.taskbotInfo;

  const orderShopInfoParams = data?.action?.buttonInfo?.action?.task_bot_action?.params;
  const order_id = orderShopInfoParams?.order_id?.int_val;
  const shop_id = orderShopInfoParams?.shop_id?.int_val;
  const orderShopInfo = { order_id, shop_id };

  addDialogue(
    {
      threadId: data?.action?.info?.threadId,
      dialogueId: data?.action?.info?.dialogueId,
      dialogueType: data?.action?.info?.dialogueType,
      threadNodeId: data?.action?.info?.threadNodeId,
      from: 'bot',
      type: 'TFEDeliveryInstructions',
      data: {
        taskbotInfo,
        cardPosition,
        action: data?.action?.buttonInfo?.action,
        orderShopInfo,
        popup: { title: data?.action?.buttonInfo?.button_text },
      },
    },
    false,
  );
});

// 设置 新的 全局的 modal 变量
eventEmitter.on('setGlobalModalConfigVisible', (bool) => {
  modalStore.SET_GLOBAL_MODAL_CONFIG_VISIBLE(bool);
});

eventEmitter.on('setGlobalModalConfigHide', (cb) => {
  modalStore.SET_GLOBAL_MODAL_CONFIG_HIDE(cb);
});

eventEmitter.on('closeGlobalModal', () => {
  const { globalModalConfig } = modalStore || {};
  const { visible, hide } = globalModalConfig || {};
  if (visible) {
    hide?.();
  }
});
